import angular from 'angular'
import api, { AbstractCollectionService } from 'components/api'
import { v4 as uuidv4 } from 'uuid'
import { mediaTraitsList } from '../../shared/media-types'

/**
 * The through table of one label namespace. The namespace is plural and the
 * table is singular, as in filemedias and medialabel_filemedia.
 */
const throughTableOf = namespace => `medialabel_${namespace.slice(0, -1)}`

/**
 * The one namespace the optimistic write below covers. The app holds the file
 * media in its state, so the library can show a new label at once.
 */
const FILE_MEDIA_NAMESPACE = 'filemedias'

/**
 * Add a label to the labels of one media, in the list the app holds.
 *
 * The write goes to the server as well, and the server reports the new row of
 * the through table. This write is the optimistic one, so the library shows the
 * label before that report arrives.
 *
 * The list can be missing. A reload straight onto a page builds the services
 * before the state of the app is filled.
 *
 * @param {Array} media - The file media the app holds, or nothing
 * @param {number} mediaId - The media that gets the label
 * @param {object} label - The label, as the label list holds it
 * @returns {object|null} The media that was changed, or null
 */
function addLabelLocally (media, mediaId, label) {
  const target = (media || []).find(one => one.id === parseInt(mediaId, 10))

  if (!target || !label) {
    return null
  }

  target.labels = target.labels || []

  if (!target.labels.some(one => one.id === label.id)) {
    target.labels.push(label)
  }

  return target
}

class MediaLabelService extends AbstractCollectionService {
  constructor ($log, $q, ApiService, StateService) {
    'ngInject'

    super($q, ApiService, StateService, 'MediaLabel', 'medialabel')
    this._log = $log
    this._listeners = {}

    // One entry and one subscription per media type. The shared traits name the
    // namespaces, so a new media type cannot be forgotten here.
    mediaTraitsList().forEach(({ labelNamespace }) => {
      this._listeners[labelNamespace] = []
      ApiService.subscribeToModel(
        throughTableOf(labelNamespace), {},
        this._mediaLabelRelationListener.bind(this, labelNamespace))
    })
  }

  _mediaLabelRelationListener (relation, error, relationInstance) {
    if (relationInstance) {
      this._listeners[relation].forEach(listener => {
        listener.callback(relationInstance)
      })
    }
  }

  getLabels (mediaType, mediaId) {
    return this.ApiService.call('medialabel/getMediaLabels', { mediaType, mediaId })
  }

  subscribeToLabelRelationChanges (mediaType, callback) {
    if (!this._listeners[mediaType]) {
      throw new Error('Unknown media type in media label service!')
    }

    const listenerUuid = uuidv4()
    this._listeners[mediaType].push({ uuid: listenerUuid, callback })

    return listenerUuid
  }

  unsubscribeToLabelRelationChanges (mediaType, listenerUuid) {
    if (!this._listeners[mediaType]) {
      throw new Error('Unknown media type in media label service!')
    }

    for (let i = this._listeners[mediaType].length - 1; i >= 0; i--) {
      if (this._listeners[mediaType][i].uuid === listenerUuid) {
        this._listeners[mediaType].splice(i, 1)
      }
    }
  }

  /**
   * Add a label to a media. `mediaType` is a label namespace, such as
   * filemedias.
   *
   * The local write is for file media only. The list of file media and the list
   * of social sources both number their rows from one, so a lookup by number
   * alone would stamp the label onto the file media with the same number.
   */
  addLabel (mediaType, mediaId, labelId) {
    const label = this.getLocal(labelId)

    if (mediaType === FILE_MEDIA_NAMESPACE) {
      const media = addLabelLocally(this.StateService.state.FileMedias, mediaId, label)

      if (media && label) {
        label[mediaType] = label[mediaType] || []

        if (!label[mediaType].some(one => one.id === media.id)) {
          label[mediaType].push({
            id: media.id,
            name: media.name,
            createdAt: media.createdAt,
            updatedAt: media.updatedAt
          })
        }
      }
    }

    return this.ApiService.call('medialabel/addMediaLabel', { mediaType, mediaId, labelId })
  }

  removeLabel (mediaType, mediaId, labelId) {
    const targetLabel = this.getLocal(labelId)
    if (targetLabel && targetLabel[mediaType]) {
      // Remove the media with the matching mediaId from the label
      targetLabel[mediaType] = targetLabel[mediaType].filter(media => media.id !== mediaId)
    }
    return this.ApiService.call('medialabel/removeMediaLabel', { mediaType, mediaId, labelId })
  }
}

export { MediaLabelService, addLabelLocally }

export default angular.module('media-label.service', [
  api
])
  .service('MediaLabelService', MediaLabelService)
  .name
